// notif-design-v2
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, CalendarClock, CheckCheck, ChevronRight, Clock, FileText, ListChecks, MoreHorizontal, RefreshCw, Receipt, UserCheck } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const POLL_MS = 60000;

function ago(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

const dot = (kind = "") =>
  kind.includes("REJECT") || kind === "ALERT" || kind === "KPI_ALERT" || kind.startsWith("LOW_") ? "bg-red-500" : kind.includes("APPROVED") ? "bg-emerald-500" : "bg-amber-500";

const FINANCE = /BILL|PAY|INVOICE|RA_|ADVANCE|DEBIT|CREDIT|PETTY/;
const SYSTEM = /^(ALERT|DEADLINE|DOC_EXPIRY|SYSTEM|KPI_ALERT)/;
const category = (kind = "") => (/^(LEAD|PROPOSAL)/.test(kind) ? "Sales" : SYSTEM.test(kind) ? "System" : FINANCE.test(kind) ? "Finance" : "Procurement");
const iconFor = (kind = "") => {
  if (kind === "ALERT" || kind === "KPI_ALERT" || kind.startsWith("LOW_")) return AlertTriangle;
  if (kind === "DEADLINE" || kind === "DOC_EXPIRY") return CalendarClock;
  if (kind.startsWith("APPROVAL")) return UserCheck;
  if (kind === "RFQ_QUOTE") return UserCheck;
  if (FINANCE.test(kind)) return Receipt;
  return FileText;
};
const TABS = ["All", "Procurement", "Sales", "Finance", "System"];
const INITIAL = 6;

export function NotificationBell({ compact = false, variant = "sidebar" }) {
  const navigate = useNavigate();
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, bottom: 0 });
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("All");
  const [showAll, setShowAll] = useState(false);
  const [menu, setMenu] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      /* the bell must never break the page */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || document.querySelector("[data-bell-portal]")?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      if (variant === "topbar") setPos({ left: Math.max(8, Math.min(r.right - 640, window.innerWidth - 656)), top: r.bottom + 6 });
      else setPos({ left: Math.min(r.right + 8, window.innerWidth - 656), bottom: Math.max(8, window.innerHeight - r.bottom) });
      load();
      setShowAll(false);
      setMenu(false);
    }
    setOpen((v) => !v);
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read_at) {
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
      setUnread((c) => Math.max(0, c - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => load());
    }
    if (n.link && n.link.startsWith("/")) navigate(n.link);
  };

  const readAll = async () => {
    setItems((list) => list.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    setUnread(0);
    api.post("/notifications/read-all").catch(() => load());
  };

  const topbar = variant === "topbar";
  const size = topbar ? "h-10 w-10" : compact ? "h-7 w-7" : "h-8 w-8";
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid="notification-bell"
        data-tour={variant === "topbar" ? "top-bell" : undefined}
        title="Notifications"
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        onClick={toggle}
        className={cn("relative flex shrink-0 items-center justify-center transition-colors", topbar ? "rounded-[10px] bg-transparent text-[#E6EBF5] hover:bg-white/10 hover:text-white" : "rounded-lg bg-[#454D5C] text-[#8B9BB8] hover:bg-[#4A5260] hover:text-[#ECF4F7]", size)}
      >
        <Bell className={topbar ? "h-5 w-5" : compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[#99002B] px-1 text-[10px] font-bold leading-none text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && createPortal(
        <div
          data-bell-portal
          data-testid="notification-panel"
          style={{ position: "fixed", left: pos.left, ...(variant === "topbar" ? { top: pos.top } : { bottom: pos.bottom }), zIndex: 9999, width: 640, maxWidth: "calc(100vw - 16px)" }}
          className="flex max-h-[80vh] flex-col overflow-hidden rounded-2xl border border-border bg-popover text-popover-foreground shadow-2xl"
        >
          <div className="flex items-center gap-3 px-5 pb-3 pt-4">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-secondary text-[#910029]">
              <Bell className="h-4 w-4" />
              {unread > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-orange-500 ring-2 ring-popover" />}
            </span>
            <h2 className="flex-1 text-base font-semibold">Notifications</h2>
            <button type="button" onClick={readAll} disabled={unread === 0} data-testid="notification-read-all" className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-[#910029] hover:bg-secondary disabled:opacity-40">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
            <div className="relative">
              <button type="button" aria-label="More" data-testid="notification-menu" onClick={() => setMenu((v) => !v)} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary">
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {menu && (
                <div className="absolute right-0 top-9 z-10 w-44 rounded-lg border border-border bg-popover p-1 text-sm shadow-lg">
                  <button type="button" className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left hover:bg-secondary" onClick={() => { setMenu(false); load(); }}>
                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                  </button>
                  <button type="button" disabled={unread === 0} className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left hover:bg-secondary disabled:opacity-40" onClick={() => { setMenu(false); readAll(); }}>
                    <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 px-5 pb-3" role="tablist">
            {TABS.map((t) => {
              const count = t === "All" ? items.length : items.filter((n) => category(n.kind) === t).length;
              const on = tab === t;
              return (
                <button key={t} type="button" role="tab" aria-selected={on} data-testid={`notification-tab-${t.toLowerCase()}`} onClick={() => { setTab(t); setShowAll(false); }}
                  className={cn("flex items-center gap-1.5 rounded-full border px-3.5 py-1 text-xs font-medium transition-colors", on ? "border-[#910029] bg-[#910029] text-white" : "border-border bg-background text-foreground hover:bg-secondary")}>
                  {t}
                  <span className={cn("rounded-full px-1.5 text-[10px] font-semibold", on ? "bg-white/20" : "bg-secondary text-muted-foreground")}>{count}</span>
                </button>
              );
            })}
          </div>
          {(() => {
            const list = items.filter((n) => tab === "All" || category(n.kind) === tab);
            const shown = showAll ? list : list.slice(0, INITIAL);
            return (
              <>
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3">
                  {!loaded ? (
                    <p className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</p>
                  ) : list.length === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">You're all caught up.</p>
                  ) : (
                    <ul className="space-y-2">
                      {shown.map((n) => {
                        const Icon = iconFor(n.kind);
                        return (
                          <li key={n.id}>
                            <button type="button" onClick={() => openItem(n)} data-testid="notification-item"
                              className={cn("flex w-full items-center gap-3.5 rounded-xl border px-3.5 py-3 text-left transition-colors hover:bg-accent/50", n.read_at ? "border-border bg-background" : "border-[#910029]/25 bg-secondary/60")}>
                              <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", n.read_at ? "bg-secondary text-muted-foreground" : "bg-[#910029]/10 text-[#910029]")}>
                                <Icon className="h-[18px] w-[18px]" />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className={cn("line-clamp-1 block text-sm", n.read_at ? "font-medium text-muted-foreground" : "font-semibold text-foreground")}>{n.title}</span>
                                {n.body && <span className="mt-0.5 line-clamp-2 block whitespace-pre-line text-xs text-muted-foreground">{n.body}</span>}
                                <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground/80"><Clock className="h-3 w-3" />{ago(n.created_at)}</span>
                              </span>
                              <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", n.read_at ? "bg-muted-foreground/25" : dot(n.kind))} />
                              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {list.length > INITIAL && (
                  <button type="button" data-testid="notification-view-all" onClick={() => setShowAll((v) => !v)}
                    className="flex items-center justify-center gap-2 border-t border-border bg-secondary/40 px-5 py-3 text-sm font-medium text-[#910029] hover:bg-secondary">
                    <ListChecks className="h-4 w-4" /> {showAll ? "Show fewer" : `View all notifications (${list.length})`} <ChevronRight className={cn("h-4 w-4", showAll && "-rotate-90")} />
                  </button>
                )}
              </>
            );
          })()}
        </div>,
        document.body
      )}
    </>
  );
}
